import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Plus, Check, ChevronDown, ChevronRight, Flag, Trash2, RefreshCw, Pencil, X, GripVertical, AlertCircle, User, Timer, Play, Square } from "lucide-react";
import DatePicker from "./DatePicker";

const PRIORITY_TAGS = {
    low: { label: "Low", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    medium: { label: "Medium", className: "bg-amber-100 text-amber-800 border-amber-200" },
    high: { label: "High", className: "bg-rose-100 text-rose-800 border-rose-200" },
};

const todayYmd = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isOverdue = (t) => !!t.due_date && t.status !== "complete" && t.due_date < todayYmd();

const fmtMins = (m) => {
    if (!m && m !== 0) return "0m";
    const mins = Math.max(0, Math.round(m));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60), rem = mins % 60;
    return rem ? `${h}h ${rem}m` : `${h}h`;
};

const newTaskForm = (extras = {}) => ({
    title: "", description: "", due_date: "", priority: "medium",
    milestone_id: "", parent_task_id: "", assignee: "", time_estimate_min: "", ...extras,
});

export default function ProjectBreakdown({ projectId, scope = "project", scopeId, onProgress }) {
    // scope/scopeId let this component drive any breakdown (project, assessment, …).
    // The projectId prop is kept for backwards compatibility with existing call-sites.
    const resolvedScope = scope || "project";
    const resolvedId = scopeId || projectId;
    const fkField = resolvedScope === "assessment" ? "assessment_id" : "project_id";

    const [bd, setBd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");          // all | outstanding | completed
    const [expandedMs, setExpandedMs] = useState({});      // ms.id -> bool
    const [expandedTasks, setExpandedTasks] = useState({});// task.id -> bool (subtasks visible)
    const [openTaskFormFor, setOpenTaskFormFor] = useState(null); // {milestoneId|"unassigned", parentTaskId|""}
    const [taskForm, setTaskForm] = useState(newTaskForm());
    const [msFormOpen, setMsFormOpen] = useState(false);
    const [msForm, setMsForm] = useState({ title: "", due_date: "" });
    const [editingTaskId, setEditingTaskId] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/breakdown?scope=${resolvedScope}&id=${encodeURIComponent(resolvedId)}`);
        setBd(data);
        onProgress?.(data);
        setLoading(false);
    };

    useEffect(() => { load(); }, [resolvedId, resolvedScope]);   // eslint-disable-line react-hooks/exhaustive-deps

    const tasks = bd?.tasks || [];
    const milestones = bd?.milestones || [];

    const passesFilter = (t) => filter === "all"
        || (filter === "outstanding" && t.status !== "complete")
        || (filter === "completed" && t.status === "complete");

    // Group tasks by milestone (milestone_id may be empty => unassigned)
    const tasksByMilestone = useMemo(() => {
        const map = {};
        for (const t of tasks) {
            if (t.parent_task_id) continue; // subtasks handled separately
            const key = t.milestone_id || "__unassigned";
            (map[key] = map[key] || []).push(t);
        }
        return map;
    }, [tasks]);

    const subtasksOf = (taskId) => tasks.filter(t => t.parent_task_id === taskId);

    const addMilestone = async (e) => {
        e.preventDefault();
        if (!msForm.title.trim()) return;
        await api.post("/milestones", {
            title: msForm.title.trim(),
            due_date: msForm.due_date || null,
            [fkField]: resolvedId,
            status: "incomplete",
        });
        setMsForm({ title: "", due_date: "" });
        setMsFormOpen(false);
        await load();
    };

    const deleteMilestone = async (id) => {
        if (!window.confirm("Delete milestone? Tasks under it will be unassigned, not deleted.")) return;
        // unassign tasks from milestone
        const affected = tasks.filter(t => t.milestone_id === id);
        await Promise.all(affected.map(t => api.put(`/tasks/${t.id}`, { milestone_id: "" })));
        await api.delete(`/milestones/${id}`);
        await load();
    };

    const addTask = async (e) => {
        e.preventDefault();
        if (!taskForm.title.trim()) return;
        await api.post("/tasks", {
            title: taskForm.title.trim(),
            description: taskForm.description || "",
            due_date: taskForm.due_date || null,
            priority: taskForm.priority || "medium",
            milestone_id: taskForm.milestone_id || "",
            parent_task_id: taskForm.parent_task_id || "",
            assignee: taskForm.assignee || "",
            time_estimate_min: taskForm.time_estimate_min ? Number(taskForm.time_estimate_min) : 0,
            time_logged_min: 0,
            [fkField]: resolvedId,
            status: "incomplete",
        });
        setOpenTaskFormFor(null);
        setTaskForm(newTaskForm());
        await load();
    };

    const toggleTask = async (t) => {
        const newStatus = t.status === "complete" ? "incomplete" : "complete";
        await api.put(`/tasks/${t.id}`, { status: newStatus });
        await load();
    };

    const deleteTask = async (t) => {
        if (!window.confirm("Delete task and any subtasks?")) return;
        const subs = subtasksOf(t.id);
        await Promise.all(subs.map(s => api.delete(`/tasks/${s.id}`)));
        await api.delete(`/tasks/${t.id}`);
        await load();
    };

    const saveEditTask = async (t, patch) => {
        await api.put(`/tasks/${t.id}`, patch);
        setEditingTaskId(null);
        await load();
    };

    const recompute = async () => {
        await api.post(`/breakdown/recompute?scope=${resolvedScope}&id=${encodeURIComponent(resolvedId)}`);
        await load();
    };

    // ---------- Drag-and-drop reorder ----------
    const dragRef = useRef(null);   // { kind, id, group? } — group is milestoneId for task drags

    const onDragStart = (kind, id, group = "") => (e) => {
        dragRef.current = { kind, id, group };
        e.dataTransfer.effectAllowed = "move";
        try { e.dataTransfer.setData("text/plain", id); } catch { /* Safari */ }
    };
    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

    const reorder = async (kind, ids) => {
        await api.post("/breakdown/reorder", { kind, ids });
        await load();
    };

    const onDropMilestone = (overMsId) => async (e) => {
        e.preventDefault();
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag || drag.kind !== "milestone" || drag.id === overMsId) return;
        const current = milestones.map(m => m.id);
        const from = current.indexOf(drag.id);
        const to = current.indexOf(overMsId);
        if (from < 0 || to < 0) return;
        current.splice(to, 0, current.splice(from, 1)[0]);
        await reorder("milestone", current);
    };

    const onDropTask = (overTaskId, overGroup) => async (e) => {
        e.preventDefault();
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag || drag.kind !== "task" || drag.id === overTaskId) return;
        // Build the ordered list of task ids in the same group (milestone or "__unassigned")
        const groupKey = overGroup;
        const groupTaskIds = (tasksByMilestone[groupKey] || []).map(t => t.id);
        let ids = [...groupTaskIds];
        // If dragging from a different group, change milestone_id first, then reorder.
        if (drag.group !== overGroup) {
            await api.put(`/tasks/${drag.id}`, { milestone_id: overGroup === "__unassigned" ? "" : overGroup });
            ids = ids.filter(id => id !== drag.id);   // ensure dragged not already in this group's list
        }
        const from = ids.indexOf(drag.id);
        const to = ids.indexOf(overTaskId);
        if (from >= 0) ids.splice(from, 1);
        const insertIdx = to >= 0 ? to : ids.length;
        ids.splice(insertIdx, 0, drag.id);
        await reorder("task", ids);
    };

    // ---------- Per-task time tracking ----------
    // Map of taskId -> startTimestamp (ms). Lives in localStorage so a refresh doesn't lose the timer.
    const TIMER_KEY = `vu_task_timers_${resolvedScope}_${resolvedId}`;
    const [timers, setTimers] = useState(() => {
        try { return JSON.parse(localStorage.getItem(TIMER_KEY) || "{}"); } catch { return {}; }
    });
    const [tick, setTick] = useState(0);
    useEffect(() => {
        localStorage.setItem(TIMER_KEY, JSON.stringify(timers));
        if (Object.keys(timers).length === 0) return;
        const id = setInterval(() => setTick(x => x + 1), 1000);
        return () => clearInterval(id);
    }, [timers, TIMER_KEY]);

    const startTimer = (taskId) =>
        setTimers(s => ({ ...s, [taskId]: Date.now() }));

    const stopTimer = async (t) => {
        const startedAt = timers[t.id];
        if (!startedAt) return;
        const elapsedMin = Math.max(1, Math.round((Date.now() - startedAt) / 60000));
        setTimers(s => { const next = { ...s }; delete next[t.id]; return next; });
        const next = (Number(t.time_logged_min) || 0) + elapsedMin;
        await api.put(`/tasks/${t.id}`, { time_logged_min: next });
        await load();
    };

    const logManualMinutes = async (t, mins) => {
        const next = (Number(t.time_logged_min) || 0) + mins;
        await api.put(`/tasks/${t.id}`, { time_logged_min: next });
        await load();
    };
    // Reference `tick` so eslint doesn't drop our 1-second re-render dependency.
    void tick;

    if (loading) return <div className="card-warm p-5 text-sm text-[var(--text-ink)]/60" data-testid="breakdown-loading">Loading breakdown…</div>;

    return (
        <div data-testid="project-breakdown">
            <SummaryBar bd={bd} onRecompute={recompute} />
            <FilterBar filter={filter} setFilter={setFilter} />

            {/* Milestones */}
            <div className="space-y-4 mt-4">
                {milestones.map(ms => {
                    const msTasks = (tasksByMilestone[ms.id] || []);
                    const completedInMs = tasks.filter(t => t.milestone_id === ms.id && t.status === "complete").length;
                    const totalInMs = tasks.filter(t => t.milestone_id === ms.id).length;
                    const expanded = expandedMs[ms.id] !== false; // default open
                    return (
                        <div key={ms.id} className="card-warm p-4" data-testid={`milestone-${ms.id}`}
                             draggable
                             onDragStart={onDragStart("milestone", ms.id)}
                             onDragOver={onDragOver}
                             onDrop={onDropMilestone(ms.id)}>
                            <MilestoneHeader
                                ms={ms}
                                expanded={expanded}
                                onToggle={() => setExpandedMs(s => ({ ...s, [ms.id]: !expanded }))}
                                completed={completedInMs} total={totalInMs}
                                onDelete={() => deleteMilestone(ms.id)}
                                onAddTask={() => { setOpenTaskFormFor({ milestoneId: ms.id, parentTaskId: "" }); setTaskForm(newTaskForm({ milestone_id: ms.id })); }}
                            />
                            {expanded && (
                                <div className="mt-3 space-y-2"
                                     onDragOver={onDragOver}
                                     onDrop={onDropTask("", ms.id)}>
                                    {msTasks.filter(passesFilter).map(t => (
                                        <TaskRow key={t.id} t={t}
                                                 subtasks={subtasksOf(t.id).filter(passesFilter)}
                                                 expanded={!!expandedTasks[t.id]}
                                                 onExpand={() => setExpandedTasks(s => ({ ...s, [t.id]: !s[t.id] }))}
                                                 onToggle={() => toggleTask(t)}
                                                 onDelete={() => deleteTask(t)}
                                                 onAddSub={() => { setOpenTaskFormFor({ milestoneId: ms.id, parentTaskId: t.id }); setTaskForm(newTaskForm({ milestone_id: ms.id, parent_task_id: t.id })); }}
                                                 onToggleSub={(s) => toggleTask(s)}
                                                 onDeleteSub={(s) => deleteTask(s)}
                                                 editing={editingTaskId === t.id}
                                                 onEdit={() => setEditingTaskId(t.id)}
                                                 onCancelEdit={() => setEditingTaskId(null)}
                                                 onSaveEdit={(patch) => saveEditTask(t, patch)}
                                                 timerStart={timers[t.id]}
                                                 onStartTimer={() => startTimer(t.id)}
                                                 onStopTimer={() => stopTimer(t)}
                                                 onLogMinutes={(m) => logManualMinutes(t, m)}
                                                 dragHandlers={{
                                                     draggable: true,
                                                     onDragStart: onDragStart("task", t.id, ms.id),
                                                     onDragOver: onDragOver,
                                                     onDrop: onDropTask(t.id, ms.id),
                                                 }} />
                                    ))}
                                    {msTasks.length === 0 && (
                                        <div className="text-xs text-[var(--text-ink)]/50 italic px-2 py-1">No tasks yet under this milestone.</div>
                                    )}
                                    {openTaskFormFor?.milestoneId === ms.id && openTaskFormFor?.parentTaskId === "" && (
                                        <TaskFormInline form={taskForm} setForm={setTaskForm} onSubmit={addTask} onCancel={() => setOpenTaskFormFor(null)} />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Unassigned tasks (no milestone) */}
                <UnassignedSection
                    tasks={(tasksByMilestone["__unassigned"] || []).filter(passesFilter)}
                    expanded={expandedMs.__unassigned !== false}
                    onToggleExpand={() => setExpandedMs(s => ({ ...s, __unassigned: s.__unassigned === false ? true : false }))}
                    subtasksOf={subtasksOf}
                    expandedTasks={expandedTasks}
                    setExpandedTasks={setExpandedTasks}
                    onToggle={toggleTask}
                    onDelete={deleteTask}
                    onAddTaskClick={() => { setOpenTaskFormFor({ milestoneId: "", parentTaskId: "" }); setTaskForm(newTaskForm()); }}
                    onAddSub={(parent) => { setOpenTaskFormFor({ milestoneId: "", parentTaskId: parent.id }); setTaskForm(newTaskForm({ parent_task_id: parent.id })); }}
                    showInlineForm={openTaskFormFor?.milestoneId === "" && openTaskFormFor?.parentTaskId === ""}
                    taskForm={taskForm}
                    setTaskForm={setTaskForm}
                    onSubmit={addTask}
                    onCancelForm={() => setOpenTaskFormFor(null)}
                    filter={filter}
                    passesFilter={passesFilter}
                    editingTaskId={editingTaskId}
                    setEditingTaskId={setEditingTaskId}
                    onSaveEdit={saveEditTask}
                    timers={timers}
                    onStartTimer={startTimer}
                    onStopTimer={stopTimer}
                    onLogMinutes={logManualMinutes}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDropTask={onDropTask}
                />

                {/* Subtask inline form (when adding to a specific parent task) */}
                {openTaskFormFor?.parentTaskId && (
                    <div className="card-warm p-4 border-2 border-[var(--brand-orange)]/30" data-testid="subtask-form">
                        <div className="text-xs uppercase tracking-wide text-[var(--brand-orange)] mb-2">New subtask</div>
                        <TaskFormInline form={taskForm} setForm={setTaskForm} onSubmit={addTask} onCancel={() => setOpenTaskFormFor(null)} />
                    </div>
                )}

                {/* Add milestone */}
                <div>
                    {!msFormOpen ? (
                        <button onClick={() => setMsFormOpen(true)} data-testid="add-milestone-btn"
                                className="btn-ghost inline-flex items-center gap-1.5 text-sm">
                            <Flag size={14} /> Add milestone
                        </button>
                    ) : (
                        <form onSubmit={addMilestone} className="card-warm p-4 grid sm:grid-cols-3 gap-2" data-testid="milestone-form">
                            <input required value={msForm.title} onChange={(e) => setMsForm({ ...msForm, title: e.target.value })}
                                   placeholder="Milestone title" data-testid="milestone-input-title"
                                   className="sm:col-span-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                            <DatePicker value={msForm.due_date} onChange={(v) => setMsForm({ ...msForm, due_date: v })}
                                        testid="milestone-input-due"
                                        placeholder="Due date" />
                            <div className="sm:col-span-3 flex justify-end gap-2">
                                <button type="button" onClick={() => setMsFormOpen(false)} className="btn-ghost">Cancel</button>
                                <button type="submit" className="btn-primary" data-testid="milestone-save-btn">Save milestone</button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryBar({ bd, onRecompute }) {
    const pct = bd?.progress || 0;
    return (
        <div className="card-warm p-5" data-testid="breakdown-summary">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="font-serif text-2xl">Project breakdown</div>
                <button onClick={onRecompute} title="Recompute progress" data-testid="recompute-btn"
                        className="btn-ghost text-xs inline-flex items-center gap-1"><RefreshCw size={12}/> Recompute</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Progress" value={`${pct}%`} testid="stat-progress" highlight />
                <Stat label="Tasks" value={`${bd?.tasks_completed || 0} / ${bd?.tasks_total || 0}`} testid="stat-tasks" />
                <Stat label="Milestones" value={`${bd?.milestones_completed || 0} / ${bd?.milestones_total || 0}`} testid="stat-milestones" />
                <Stat label="Outstanding" value={(bd?.tasks_total || 0) - (bd?.tasks_completed || 0)} testid="stat-outstanding" />
            </div>
            <div className="mt-3 h-3 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                <div className="h-full brand-gradient transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="progress-bar" />
            </div>
        </div>
    );
}

function Stat({ label, value, testid, highlight }) {
    return (
        <div data-testid={testid} className={`rounded-xl px-3 py-2 ${highlight ? "brand-gradient text-white" : "bg-[rgba(26,19,24,0.04)]"}`}>
            <div className={`text-[10px] uppercase tracking-wider ${highlight ? "text-white/80" : "text-[var(--text-ink)]/55"}`}>{label}</div>
            <div className="font-serif text-xl">{value}</div>
        </div>
    );
}

function FilterBar({ filter, setFilter }) {
    return (
        <div className="flex gap-1.5 mt-3" data-testid="filter-bar">
            {[
                ["all", "All"],
                ["outstanding", "Outstanding"],
                ["completed", "Completed"],
            ].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                        className={`text-xs px-3 py-1.5 rounded-full transition ${filter === k ? "brand-gradient text-white" : "btn-ghost"}`}>
                    {label}
                </button>
            ))}
        </div>
    );
}

function MilestoneHeader({ ms, expanded, onToggle, completed, total, onDelete, onAddTask }) {
    const allDone = total > 0 && completed === total;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <GripVertical size={14} className="text-[var(--text-ink)]/30 cursor-grab active:cursor-grabbing" data-testid={`ms-drag-${ms.id}`} aria-label="Drag to reorder" />
            <button onClick={onToggle} className="text-[var(--text-ink)]/55 hover:text-[var(--brand-orange)]" data-testid={`ms-toggle-${ms.id}`}>
                {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            <Flag size={16} className={allDone ? "text-emerald-600" : "text-[var(--brand-orange)]"} />
            <div className={`font-serif text-lg ${allDone ? "line-through text-[var(--text-ink)]/40" : ""}`}>{ms.title}</div>
            {ms.due_date && <span className="text-xs text-[var(--text-ink)]/55">· due {ms.due_date}</span>}
            <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(26,19,24,0.06)]" data-testid={`ms-count-${ms.id}`}>{completed}/{total}</span>
            <div className="ml-auto flex gap-1">
                <button onClick={onAddTask} data-testid={`ms-add-task-${ms.id}`} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12}/> Task</button>
                <button onClick={onDelete} data-testid={`ms-delete-${ms.id}`} className="btn-ghost text-xs text-rose-700" title="Delete milestone"><Trash2 size={14}/></button>
            </div>
        </div>
    );
}

function TaskRow({ t, subtasks, expanded, onExpand, onToggle, onDelete, onAddSub, onToggleSub, onDeleteSub, editing, onEdit, onCancelEdit, onSaveEdit, timerStart, onStartTimer, onStopTimer, onLogMinutes, dragHandlers = {} }) {
    const isDone = t.status === "complete";
    const hasSubs = subtasks && subtasks.length > 0;
    const overdue = isOverdue(t);
    const running = !!timerStart;
    const liveMin = running ? Math.max(0, Math.floor((Date.now() - timerStart) / 60000)) : 0;
    const liveSec = running ? Math.max(0, Math.floor((Date.now() - timerStart) / 1000) % 60) : 0;
    const logged = Number(t.time_logged_min) || 0;
    const estimate = Number(t.time_estimate_min) || 0;
    return (
        <div className={`rounded-xl bg-white/60 border ${overdue ? "border-rose-300" : "border-[rgba(26,19,24,0.08)]"} ${running ? "ring-2 ring-[var(--brand-orange)]/30" : ""}`}
             data-testid={`task-${t.id}`} {...dragHandlers}>
            <div className="flex items-start gap-2 p-2.5">
                <GripVertical size={12} className="mt-1 text-[var(--text-ink)]/25 cursor-grab active:cursor-grabbing shrink-0" data-testid={`task-drag-${t.id}`} aria-label="Drag to reorder" />
                <Checkbox checked={isDone} onChange={onToggle} testid={`task-check-${t.id}`} />
                <div className="flex-1 min-w-0">
                    {!editing ? (
                        <>
                            <div className={`flex items-center gap-2 flex-wrap ${isDone ? "line-through text-[var(--text-ink)]/45" : ""}`}>
                                <span className="font-medium" data-testid={`task-title-${t.id}`}>{t.title}</span>
                                {t.priority && <PriorityChip priority={t.priority} />}
                                {t.due_date && <span className="text-[10px] text-[var(--text-ink)]/55">due {t.due_date}</span>}
                                {overdue && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200" data-testid={`task-overdue-${t.id}`}>
                                        <AlertCircle size={9}/> Overdue
                                    </span>
                                )}
                                {t.assignee && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--brand-purple)]/10 text-[var(--brand-purple)] border border-[var(--brand-purple)]/20" data-testid={`task-assignee-${t.id}`}>
                                        <User size={9}/> {t.assignee}
                                    </span>
                                )}
                                {(logged > 0 || estimate > 0 || running) && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--brand-orange)]/10 text-[var(--brand-orange)] border border-[var(--brand-orange)]/20" data-testid={`task-time-${t.id}`}
                                          title={`${fmtMins(logged)} logged${estimate ? ` of ${fmtMins(estimate)}` : ""}${running ? " · running" : ""}`}>
                                        <Timer size={9}/> {fmtMins(logged + liveMin)}{estimate ? ` / ${fmtMins(estimate)}` : ""}{running ? ` · ${String(liveMin).padStart(2,'0')}:${String(liveSec).padStart(2,'0')}` : ""}
                                    </span>
                                )}
                            </div>
                            {t.description && <div className="text-xs text-[var(--text-ink)]/60 mt-1 whitespace-pre-wrap">{t.description}</div>}
                            {estimate > 0 && (
                                <div className="mt-1 h-1 rounded-full bg-[rgba(26,19,24,0.06)] overflow-hidden" data-testid={`task-time-bar-${t.id}`}>
                                    <div className="h-full bg-[var(--brand-orange)]" style={{ width: `${Math.min(100, Math.round(((logged + liveMin) / estimate) * 100))}%` }} />
                                </div>
                            )}
                        </>
                    ) : (
                        <TaskEditInline t={t} onCancel={onCancelEdit} onSave={onSaveEdit} />
                    )}
                </div>
                {!editing && (
                    <div className="flex gap-1 shrink-0">
                        {!isDone && (
                            running ? (
                                <button onClick={onStopTimer} title="Stop timer" data-testid={`task-stop-${t.id}`} className="btn-ghost text-xs text-[var(--brand-orange)]"><Square size={12} fill="currentColor"/></button>
                            ) : (
                                <button onClick={onStartTimer} title="Start timer" data-testid={`task-start-${t.id}`} className="btn-ghost text-xs"><Play size={12}/></button>
                            )
                        )}
                        {!isDone && (
                            <button onClick={() => onLogMinutes(15)} title="Log 15 min" data-testid={`task-log15-${t.id}`} className="btn-ghost text-[10px]">+15m</button>
                        )}
                        <button onClick={onAddSub} title="Add subtask" data-testid={`task-add-sub-${t.id}`} className="btn-ghost text-xs"><Plus size={12}/></button>
                        <button onClick={onEdit} title="Edit task" data-testid={`task-edit-${t.id}`} className="btn-ghost text-xs"><Pencil size={12}/></button>
                        <button onClick={onDelete} title="Delete task" data-testid={`task-delete-${t.id}`} className="btn-ghost text-xs text-rose-700"><Trash2 size={12}/></button>
                        {hasSubs && (
                            <button onClick={onExpand} data-testid={`task-expand-${t.id}`} className="btn-ghost text-xs">
                                {expanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>} {subtasks.length}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {hasSubs && expanded && (
                <div className="pl-8 pr-2 pb-2 space-y-1.5">
                    {subtasks.map(s => (
                        <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`subtask-${s.id}`}>
                            <Checkbox checked={s.status === "complete"} onChange={() => onToggleSub(s)} testid={`subtask-check-${s.id}`} size="sm" />
                            <span className={`flex-1 ${s.status === "complete" ? "line-through text-[var(--text-ink)]/45" : ""}`}>{s.title}</span>
                            {s.priority && <PriorityChip priority={s.priority} small />}
                            <button onClick={() => onDeleteSub(s)} className="btn-ghost text-xs text-rose-700" data-testid={`subtask-delete-${s.id}`}><Trash2 size={11}/></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Checkbox({ checked, onChange, testid, size = "md" }) {
    const dim = size === "sm" ? "w-4 h-4" : "w-5 h-5";
    return (
        <button onClick={onChange} data-testid={testid}
                className={`${dim} rounded-md border-2 inline-flex items-center justify-center transition ${checked ? "bg-emerald-500 border-emerald-500" : "border-[rgba(26,19,24,0.25)] hover:border-[var(--brand-orange)]"}`}>
            {checked && <Check size={size === "sm" ? 11 : 13} className="text-white" />}
        </button>
    );
}

function PriorityChip({ priority, small }) {
    const cfg = PRIORITY_TAGS[priority] || PRIORITY_TAGS.medium;
    return (
        <span data-testid={`priority-${priority}`} className={`inline-block ${small ? "text-[9px] px-1.5 py-0" : "text-[10px] px-2 py-0.5"} rounded-full border ${cfg.className}`}>
            {cfg.label}
        </span>
    );
}

function TaskFormInline({ form, setForm, onSubmit, onCancel }) {
    return (
        <form onSubmit={onSubmit} className="grid sm:grid-cols-6 gap-2 p-2 rounded-xl bg-[rgba(255,107,53,0.05)]" data-testid="task-form">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Task title" data-testid="task-input-title"
                   className="sm:col-span-3 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            <div className="sm:col-span-2">
                <DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })}
                            testid="task-input-due" placeholder="Due date" />
            </div>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    data-testid="task-input-priority"
                    className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
            </select>
            <input value={form.assignee || ""} onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                   placeholder="Assignee (e.g. Me, Sarah)" data-testid="task-input-assignee"
                   className="sm:col-span-3 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            <input type="number" min="0" step="5" value={form.time_estimate_min || ""}
                   onChange={(e) => setForm({ ...form, time_estimate_min: e.target.value })}
                   placeholder="Estimate (min)" data-testid="task-input-estimate"
                   className="sm:col-span-3 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Description (optional)" data-testid="task-input-desc"
                      className="sm:col-span-6 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            <div className="sm:col-span-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-ghost text-sm" data-testid="task-cancel-btn"><X size={14}/></button>
                <button type="submit" className="btn-primary text-sm" data-testid="task-save-btn"><Plus size={14} className="inline" /> Add</button>
            </div>
        </form>
    );
}

function TaskEditInline({ t, onCancel, onSave }) {
    const [f, setF] = useState({
        title: t.title || "", description: t.description || "",
        due_date: t.due_date || "", priority: t.priority || "medium",
        assignee: t.assignee || "", time_estimate_min: t.time_estimate_min || "",
    });
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...f, time_estimate_min: f.time_estimate_min ? Number(f.time_estimate_min) : 0 }); }} className="grid sm:grid-cols-6 gap-2" data-testid={`task-edit-form-${t.id}`}>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                   data-testid={`task-edit-title-${t.id}`}
                   className="sm:col-span-3 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
            <div className="sm:col-span-2">
                <DatePicker value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })}
                            testid={`task-edit-due-${t.id}`} placeholder="Due date" />
            </div>
            <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}
                    className="px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })}
                   placeholder="Assignee" data-testid={`task-edit-assignee-${t.id}`}
                   className="sm:col-span-3 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
            <input type="number" min="0" step="5" value={f.time_estimate_min}
                   onChange={(e) => setF({ ...f, time_estimate_min: e.target.value })}
                   placeholder="Estimate (min)" data-testid={`task-edit-estimate-${t.id}`}
                   className="sm:col-span-3 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                      className="sm:col-span-6 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
            <div className="sm:col-span-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
                <button type="submit" className="btn-primary text-xs" data-testid={`task-edit-save-${t.id}`}>Save</button>
            </div>
        </form>
    );
}

function UnassignedSection({
    tasks, expanded, onToggleExpand, subtasksOf, expandedTasks, setExpandedTasks,
    onToggle, onDelete, onAddTaskClick, onAddSub,
    showInlineForm, taskForm, setTaskForm, onSubmit, onCancelForm,
    editingTaskId, setEditingTaskId, onSaveEdit,
    timers, onStartTimer, onStopTimer, onLogMinutes,
    onDragStart, onDragOver, onDropTask,
}) {
    return (
        <div className="card-warm p-4" data-testid="unassigned-section">
            <div className="flex items-center gap-2">
                <button onClick={onToggleExpand} className="text-[var(--text-ink)]/55 hover:text-[var(--brand-orange)]">
                    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <div className="font-serif text-lg">Standalone tasks</div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(26,19,24,0.06)]">{tasks.length}</span>
                <button onClick={onAddTaskClick} className="ml-auto btn-ghost text-xs inline-flex items-center gap-1" data-testid="add-unassigned-task-btn"><Plus size={12}/> Task</button>
            </div>
            {expanded && (
                <div className="mt-3 space-y-2"
                     onDragOver={onDragOver}
                     onDrop={onDropTask("", "__unassigned")}>
                    {tasks.map(t => (
                        <TaskRow key={t.id} t={t}
                                 subtasks={subtasksOf(t.id)}
                                 expanded={!!expandedTasks[t.id]}
                                 onExpand={() => setExpandedTasks(s => ({ ...s, [t.id]: !s[t.id] }))}
                                 onToggle={() => onToggle(t)}
                                 onDelete={() => onDelete(t)}
                                 onAddSub={() => onAddSub(t)}
                                 onToggleSub={(s) => onToggle(s)}
                                 onDeleteSub={(s) => onDelete(s)}
                                 editing={editingTaskId === t.id}
                                 onEdit={() => setEditingTaskId(t.id)}
                                 onCancelEdit={() => setEditingTaskId(null)}
                                 onSaveEdit={(patch) => onSaveEdit(t, patch)}
                                 timerStart={timers?.[t.id]}
                                 onStartTimer={() => onStartTimer(t.id)}
                                 onStopTimer={() => onStopTimer(t)}
                                 onLogMinutes={(m) => onLogMinutes(t, m)}
                                 dragHandlers={{
                                     draggable: true,
                                     onDragStart: onDragStart("task", t.id, "__unassigned"),
                                     onDragOver: onDragOver,
                                     onDrop: onDropTask(t.id, "__unassigned"),
                                 }} />
                    ))}
                    {tasks.length === 0 && (
                        <div className="text-xs text-[var(--text-ink)]/50 italic px-2 py-1">No standalone tasks.</div>
                    )}
                    {showInlineForm && (
                        <TaskFormInline form={taskForm} setForm={setTaskForm} onSubmit={onSubmit} onCancel={onCancelForm} />
                    )}
                </div>
            )}
        </div>
    );
}
